/**
 * The in-game **reconnect overlay** (ADR-0111).
 *
 * When the gameplay socket drops after the splash has been submitted, the session
 * freezes: remotes stop updating, local prediction is halted, and `main.ts` starts
 * re-running the login handshake on exponential backoff (the pure `planReconnect`
 * policy in `src/reconnect.ts`). This overlay is what the player sees during that
 * outage: a translucent veil over the frozen world with a spinner and a visible
 * "Reconnecting…" line, so a drop reads as "the client is on it" rather than a
 * silently dead room. A successful re-Welcome hides it; an outage that outlives
 * the policy budget flips it to an error/retry block (Retry reloads the page,
 * exactly like the first-visit loading overlay's).
 *
 * Unlike `src/loading.ts` this overlay is **not** one-shot: show/hide can cycle
 * (drop → reconnect → later drop → reconnect), so `show()` re-arms the spinner
 * state even after a `fail()`. Like the splash/HUD/chat modules it owns only the
 * DOM wiring; the markup and styling live in `index.html`. It reads no Pixi and
 * no socket.
 */

/** Optional hooks for {@link initReconnectOverlay}; all optional so headless tests pass none. */
export interface ReconnectOverlayOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
  /**
   * What the error-state **Retry** button does. Defaults to a full page reload,
   * which re-runs `main()` from scratch - the same simplest-reliable retry the
   * loading overlay uses. Injected by tests so a click is observable without
   * navigating the jsdom window.
   */
  onRetry?: () => void;
}

/** The handle `main.ts` drives the reconnect overlay through. */
export interface ReconnectOverlayController {
  /**
   * Show the reconnecting state (spinner + "Reconnecting…"). Re-arms the spinner
   * even after a {@link fail}, so a fresh outage never shows a stale error.
   */
  show(): void;
  /** Dismiss the overlay: the connection is live again (a Welcome arrived). */
  hide(): void;
  /**
   * Flip the overlay to its error/retry state with `message`: the outage outlived
   * the reconnect budget, so retrying stopped and the player must act.
   */
  fail(message: string): void;
}

/**
 * Wire the `#reconnect` overlay (declared **hidden** in `index.html` - unlike
 * `#loading` it only ever appears mid-session) into a handle `main.ts` shows on a
 * gameplay-socket drop, hides on the re-Welcome, and flips to error/retry when the
 * outage outlives the policy budget. The element and its parts
 * (`#reconnect-spinner`, `#reconnect-status`, `#reconnect-error`,
 * `#reconnect-error-message`, `#reconnect-retry`) are looked up once; any that are
 * missing are tolerated (headless tests mount a subset), so partial markup never
 * throws. Returns a no-op handle when the overlay element itself is absent.
 */
export function initReconnectOverlay(opts: ReconnectOverlayOptions = {}): ReconnectOverlayController {
  const doc = opts.doc ?? document;
  const onRetry = opts.onRetry ?? (() => location.reload());
  const root = doc.getElementById('reconnect');
  const spinner = doc.getElementById('reconnect-spinner');
  const status = doc.getElementById('reconnect-status');
  const error = doc.getElementById('reconnect-error');
  const errorMessage = doc.getElementById('reconnect-error-message');
  const retry = doc.getElementById('reconnect-retry');

  retry?.addEventListener('click', () => onRetry());

  return {
    show() {
      if (!root) return;
      // Re-arm the reconnecting state: spinner + status up, error block down - so
      // a show() on a later outage (after a fail() the player rode out without
      // reloading) reads as reconnecting again, never as the stale error.
      if (spinner) spinner.hidden = false;
      if (status) status.hidden = false;
      if (error) error.hidden = true;
      root.hidden = false;
    },
    hide() {
      if (root) root.hidden = true;
    },
    fail(message) {
      if (!root) return;
      // Swap the spinner for the error block, keeping the overlay up so the player
      // sees why the room is frozen and can retry rather than staring at a dead
      // spinner - the same shape as the loading overlay's fail state.
      if (spinner) spinner.hidden = true;
      if (status) status.hidden = true;
      if (errorMessage) errorMessage.textContent = message;
      if (error) error.hidden = false;
      root.hidden = false;
    },
  };
}
